import { useMemo, useState } from "react";
import { Switch, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import AddButton from "../../components/addButton";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import { useGetBanksQuery, useToggleBankStatusMutation } from "../../services/banks";
import { AddBankRoute, BankDepositHistoryRoute } from "../../routes/routepath";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface BankRecord {
  key:            string;
  bankName?:      string;
  accountNumber?: string;
  ifscCode?:      string;
  branchNames?:   string[];
  isActive?:      boolean;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const AllBank = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(10);
  const [searchText, setSearchText] = useState("");
  const [activeTab,  setActiveTab]  = useState("all");

  const { data: banksResp, isFetching: banksLoading } = useGetBanksQuery({
    search: searchText || undefined,
    status: activeTab as 'all' | 'active' | 'inactive',
    page,
    limit,
  });
  const [toggleBankStatus, { isLoading: toggling }] = useToggleBankStatusMutation();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const apiData = banksResp as any;
  const counts  = apiData?.counts || {};

  const banks = useMemo<BankRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((b: any) => ({
      key:           b._id,
      bankName:      b.bankName,
      accountNumber: b.accountNumber,
      ifscCode:      b.ifscCode,
      branchNames:   Array.isArray(b.branchIds) ? b.branchIds.map((br: any) => br?.name).filter(Boolean) : (b.branchCity ? [b.branchCity] : []),
      isActive:      b.isActive,
    }));
  }, [apiData]);

  const tabsData = [
    { key: 'all',      label: 'All',      count: counts.all      || 0 },
    { key: 'active',   label: 'Active',   count: counts.active   || 0 },
    { key: 'inactive', label: 'Inactive', count: counts.inactive || 0 },
  ];

  const canToggle = hasPermission('BANK_STATUS_TOGGLE');

  const handleToggle = async (record: BankRecord, checked: boolean) => {
    if (!canToggle) return;
    setTogglingId(record.key);
    try {
      await toggleBankStatus({ id: record.key, isActive: checked }).unwrap();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    { title: 'S.No',           key: 'sno', width: 60, render: (_: unknown, __: unknown, i: number) => (page - 1) * limit + i + 1 },
    { title: 'Bank Name',      dataIndex: 'bankName',      key: 'bankName',      render: (v?: string) => v || '-' },
    { title: 'Account Number', dataIndex: 'accountNumber', key: 'accountNumber',
      render: (v: string, record: BankRecord) => v ? (
        <span
          style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`${BankDepositHistoryRoute}?accountNumber=${encodeURIComponent(v)}`, { state: { bankName: record.bankName } })}
        >
          {v}
        </span>
      ) : '-' },
    { title: 'IFSC Code',      dataIndex: 'ifscCode',      key: 'ifscCode',      render: (v?: string) => v || '-' },
    { title: 'Branch',         dataIndex: 'branchNames',   key: 'branchNames',
      render: (names?: string[]) => {
        if (!names || names.length === 0) return '-';
        const rest = names.slice(1);
        return (
          <span>
            {names[0]}
            {rest.length > 0 && (
              <Tooltip title={rest.join(', ')}>
                <span style={{ marginLeft: 6, color: 'var(--accent)', cursor: 'pointer' }}>+{rest.length}</span>
              </Tooltip>
            )}
          </span>
        );
      } },
    { title: 'Status', dataIndex: 'isActive', key: 'isActive', align: 'center' as const,
      render: (isActive: boolean, record: BankRecord) => (
        <Switch
          checked={isActive}
          disabled={!canToggle}
          loading={toggling && togglingId === record.key}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ) },
  ];

  return (
    <div className="all-bank-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(v: string) => { setSearchText(v); setPage(1); }}
            placeholder="Search banks..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={tabsData}
          />
        </div>
        <div className="right-col">
          {hasPermission('BANK_CREATE') && (
            <AddButton to={AddBankRoute}>Add Bank</AddButton>
          )}
        </div>
      </div>

      <div className="banks-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={banks}
          loading={banksLoading}
          pagination={false}
          rowKey={(record: BankRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default AllBank;
